import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Users } from "lucide-react";
import { useJobOverrides } from "@/hooks/useCommissionOverrides";
import { useCompanyTax } from "@/hooks/useCompanyTax";
import { resolveTaxConfig } from "@/utils/tax";
import type { Job } from "@/types";

interface JobOverrideCommissionCardProps {
  job: Job;
}

export function JobOverrideCommissionCard({ job }: JobOverrideCommissionCardProps) {
  const { data: overrides, isLoading } = useJobOverrides(job.id);
  const liveTax = useCompanyTax();
  const tax = resolveTaxConfig(job, liveTax);
  
  // Calculate Net Revenue
  const netRevenue = (job.revenue_before_gst || 0) 
    - (job.total_parts_cost || 0) 
    - (job.toll_cost || 0);
  
  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">Override Commissions</h3>
        </div>
        <p className="text-sm text-muted-foreground mt-4">Loading override commissions...</p>
      </Card>
    );
  }
  
  if (!overrides || overrides.length === 0) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">Override Commissions</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          No override commissions generated for this job.
        </p>
      </Card>
    );
  }
  
  return (
    <Card className="p-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Users className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Override Commissions Generated</h3>
      </div>
      
      {/* Net Revenue Breakdown */}
      <div className="bg-muted/50 p-4 rounded-lg mb-4">
        <h4 className="text-sm font-semibold text-muted-foreground mb-3">
          NET REVENUE CALCULATION
        </h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>{tax.enabled ? `Revenue (ex. ${tax.label}):` : 'Net Revenue:'}</span>
            <span className="font-medium">
              ${(job.revenue_before_gst || 0).toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Less Parts Cost:</span>
            <span className="font-medium text-destructive">
              -${(job.total_parts_cost || 0).toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Less Tolls:</span>
            <span className="font-medium text-destructive">
              -${(job.toll_cost || 0).toFixed(2)}
            </span>
          </div>
          <Separator />
          <div className="flex justify-between font-semibold">
            <span>Net Revenue:</span>
            <span className="text-lg text-primary">
              ${netRevenue.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
      
      {/* Overrides Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Beneficiary Technician</TableHead>
              <TableHead className="text-right">Override Rate</TableHead>
              <TableHead className="text-right">Override Amount</TableHead>
              <TableHead className="text-center">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {overrides.map((override) => (
              <TableRow key={override.id}>
                <TableCell className="font-medium">
                  {override.beneficiary_tech?.name || 'Unknown'}
                </TableCell>
                <TableCell className="text-right">
                  {override.override_rate_percentage.toFixed(2)}%
                </TableCell>
                <TableCell className="text-right font-semibold">
                  ${override.override_amount.toFixed(2)}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={
                    override.commission_status === 'paid' ? 'default' :
                    override.commission_status === 'approved' ? 'secondary' :
                    'outline'
                  }>
                    {override.commission_status}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      
      {/* Total */}
      <div className="flex justify-between items-center mt-4 pt-4 border-t">
        <span className="text-lg font-semibold">Total Override Commission:</span>
        <span className="text-2xl font-bold text-primary">
          ${(job.total_override_commission || 0).toFixed(2)}
        </span>
      </div>
    </Card>
  );
}
